import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { PaymentRequestService } from 'src/app/pages/fundraiser/services/payment-request.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { environment } from 'src/environments/environment';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SafeHtml } from '@angular/platform-browser';
import { CustomDomainService } from 'src/app/global/services/custom-domain.service';
import { FundraiserService } from 'src/app/pages/fundraiser/services/fundraiser.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgClass } from '@angular/common';
import { SocialShareButtonsComponent } from '../../../../../../shared/components/social-share-buttons/social-share-buttons.component';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { FlexModule } from '@angular/flex-layout/flex';

@Component({
	selector: 'app-share-fundraiser-page',
	templateUrl: './share-fundraiser-page.component.html',
	styleUrls: ['./share-fundraiser-page.component.scss'],
	standalone: true,
	imports: [
		FlexModule,
		ExtendedModule,
		SocialShareButtonsComponent,
		NgIf,
		NgClass,
		MatButtonModule,
		MatTooltipModule,
		MatIconModule,
	],
})
//** *Share Fundraiser */
export class ShareFundraiserPageComponent implements OnInit {
	shareLink: string = '';
	@Input() disablePayment: boolean = false;
	@Input() type: string | any = '';
	@Input() preview: boolean = false;
	@Input() applyPadding: boolean = true; // Set to true by default
	oneTimeLabel = $localize`:@@embed_oneTimeLabel:One Time`;
	monthlyLabel = $localize`:@@embed_monthlyLabel:Monthly`;
	yearlyLabel = $localize`:@@embed_yearlyLabel:Yearly`;
	matTooltip = $localize`:@@share_fundraiser_tooltip_copy:Copy share fundraiser link`;
	currency_symbol: string = '';
	onetime_first: string = '';
	onetime_second: string = '';
	onetime_third: string = '';
	onetime_forth: string = '';
	monthly_first: string = '';
	monthly_second: string = '';
	monthly_third: string = '';
	monthly_forth: string = '';
	yearly_first: string = '';
	yearly_second: string = '';
	yearly_third: string = '';
	yearly_forth: string = '';
	fundraiserBackgroundImage: string = '';
	fundraiserBackgroundVideo: string = '';
	video: string = '';
	youtubeThumbnail: any;
	videoPath!: any;

	constructor(
		public router: Router,
		public clipboard: Clipboard,
		public dialog: MatDialog,
		public paymentRequest: PaymentRequestService,
		public media: MediaObserver,
		private _notificationService: NotificationService,
		public _accountService: AccountService,
		@Inject(LOCALE_ID) public locale: string,
		@Inject(MAT_DIALOG_DATA) public data: { fundraiser: any },
		public dialogRef: MatDialogRef<ShareFundraiserPageComponent>,
		public customDomainService: CustomDomainService,
		public fundraiserService: FundraiserService
	) {}

	ngOnInit(): void {
		// Initialize fundraiser data
		this.initializeFundraiserData();

		const url: any = this.fundraiserBackgroundVideo;
		this.youtubeThumbnail = this.checkVideoUrl(url);
		this.videoPath = url;

		// Generate share link based on custom domain or default domain
		this.generateShareLink();
	}

	/**
	 * Initialize fundraiser data
	 */
	private initializeFundraiserData(): void {
		this.currency_symbol = this.data?.fundraiser?.currency_symbol;
		this.onetime_first =
			this.data?.fundraiser?.custom_donation_configuration?.onetime_first;
		this.onetime_second =
			this.data?.fundraiser?.custom_donation_configuration?.onetime_second;
		this.onetime_third =
			this.data?.fundraiser?.custom_donation_configuration?.onetime_third;
		this.onetime_forth =
			this.data?.fundraiser?.custom_donation_configuration?.onetime_forth;
		this.monthly_first =
			this.data?.fundraiser?.custom_donation_configuration?.monthly_first;
		this.monthly_second =
			this.data?.fundraiser?.custom_donation_configuration?.monthly_second;
		this.monthly_third =
			this.data?.fundraiser?.custom_donation_configuration?.monthly_third;
		this.monthly_forth =
			this.data?.fundraiser?.custom_donation_configuration?.monthly_forth;
		this.yearly_first =
			this.data?.fundraiser?.custom_donation_configuration?.yearly_first;
		this.yearly_second =
			this.data?.fundraiser?.custom_donation_configuration?.yearly_second;
		this.yearly_third =
			this.data?.fundraiser?.custom_donation_configuration?.yearly_third;
		this.yearly_forth =
			this.data?.fundraiser?.custom_donation_configuration?.yearly_forth;

		// Set fundraiser background image and video
		if (
			(this.data?.fundraiser?.background?.image != '' &&
				this.data?.fundraiser?.background?.video != '') ||
			(this.data?.fundraiser?.background?.image == '' &&
				this.data?.fundraiser?.background?.video != '') ||
			(this.data?.fundraiser?.background?.image != '' &&
				this.data?.fundraiser?.background?.video == '')
		) {
			this.fundraiserBackgroundImage = this.data?.fundraiser?.background?.image;
			this.fundraiserBackgroundVideo = this.data?.fundraiser?.background?.video;
		} else {
			this.fundraiserBackgroundImage =
				this.data?.fundraiser?.parent?.background?.image;
			this.fundraiserBackgroundVideo =
				this.data?.fundraiser?.parent?.background?.video;
		}
	}

	/**
	 * Generate share link based on custom domain or default domain
	 */
	private generateShareLink(): void {
		this.customDomainService.isCustomDomain.subscribe(
			(value: any) => {
				try {
					const hostname = window.location.hostname;
					const locale = this._accountService.getLocaleId();
					const splitted = this.router.url.split('/');

					if (value) {
						// Custom domain logic
						this.shareLink = this.generateCustomDomainLink(
							hostname,
							locale,
							splitted
						);
					} else {
						// Default domain logic
						const homeLink = environment.homeUrl;
						this.shareLink = this.generateDefaultDomainLink(
							homeLink,
							locale,
							splitted
						);
					}
				} catch (error) {
					console.error('Error generating share link:', error);
				}
			},
			(error: any) => {
				console.error('Error subscribing to custom domain service:', error);
			}
		);
	}

	/**
	 * Generate share link for custom domain
	 * @param hostname - The hostname of the custom domain
	 * @param locale - The locale ID
	 * @param splitted - The splitted URL segments
	 * @returns The generated share link
	 */
	private generateCustomDomainLink(
		hostname: string,
		locale: string,
		splitted: string[]
	): string {
		if (this.type === 'donate') {
			let slug = this.fundraiserService.getCurrentFundraiserSlug();
			return `https://${hostname}/${locale}/${this.type}/${slug}`;
		} else if (this.type === 'fundraiser') {
			return window.location.href;
		} else {
			this.paymentRequest.currentCustomText.subscribe((data) => {
				this.shareLink = data;
			});
			return this.shareLink;
		}
	}

	/**
	 * Generate share link for default domain
	 * @param homeLink - The home URL of the default domain
	 * @param locale - The locale ID
	 * @param splitted - The splitted URL segments
	 * @returns The generated share link
	 */
	private generateDefaultDomainLink(
		homeLink: string,
		locale: string,
		splitted: string[]
	): string {
		const isDefaultLocale = locale === 'en';

		if (this.type === 'donate') {
			return isDefaultLocale
				? `${homeLink}/${this.type}/${splitted[2]}`
				: `${homeLink}/${locale}/${this.type}/${splitted[2]}`;
		} else if (this.type === 'fundraiser') {
			return isDefaultLocale
				? `${homeLink}/${splitted[1]}/${splitted[2]}`
				: `${homeLink}/${locale}/${splitted[1]}/${splitted[2]}`;
		} else {
			this.paymentRequest.currentCustomText.subscribe((data) => {
				// Strip '/en/' if locale is default
				if (locale === 'en' && data.includes('/en/')) {
					this.shareLink = data.replace('/en/', '/');
				} else {
					this.shareLink = data;
				}
			});
			return this.shareLink;
		}
	}

	/**
	 * Truncate text based on the screen size
	 * @param text - The text to truncate
	 * @param maxLength - The maximum length of the text
	 * @returns The truncated text
	 */
	truncateText(text: string, maxLength: number): string {
		if (this.media.isActive('xs')) {
			maxLength = 35;
			return text.slice(0, maxLength) + '...';
		} else {
			return text;
		}
	}

	/**
	 * Copy the share link to the clipboard
	 */
	copyLink(): void {
		this.clipboard.copy(this.shareLink);
		this._notificationService.openNotification(
			$localize`:@@share_fundraiser_link:The Link is successfully copied`,
			'OK',
			'success'
		);
	}

	/**
	 * Print the fundraiser page
	 */
	printFundraiser(): void {
		window.print();
	}

	/**
	 * Check the video URL and return the appropriate thumbnail
	 * @param url - The video URL
	 * @returns The video thumbnail URL
	 */
	checkVideoUrl(url: any): SafeHtml {
		const vimeoId = this.getVideoIdFromLink(
			url,
			/^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/
		);
		const youtubeId = this.getVideoIdFromLink(
			url,
			/^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:embed\/|watch\?v=|v\/|shorts\/)|youtu\.be\/)([\w\-]+)(?:\S+)?$/
		);

		if (vimeoId) {
			return `https://vumbnail.com/${vimeoId}.jpg`;
		} else if (youtubeId) {
			return `https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`;
		} else {
			return 'unknown';
		}
	}

	/**
	 * Extract video ID from the URL based on the pattern
	 * @param url - The video URL
	 * @param pattern - The regex pattern to extract the video ID
	 * @returns The extracted video ID
	 */
	getVideoIdFromLink(url: string, pattern: RegExp): string {
		const match = url?.match(pattern);
		return match && match[1] ? match[1] : '';
	}
}
